import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { configAPI, normalizeAnnouncements, type PlatformAnnouncement } from '../api/config';
import { AnnouncementDetailModal } from '../components/news/AnnouncementDetailModal';
import { EmptyState } from '../components/ui/EmptyState';
import { LoadingSkeleton } from '../components/ui/LoadingSkeleton';
import { ThemedPageHeader } from '../components/ui/ThemedPageHeader';
import { THEMED_SURFACE_COLORS as C, ThemedChip, ThemedPanel } from '../components/ui/ThemedSurface';
import { formatAnnouncementDate } from '../utils/announcement-display';

type Announcement = PlatformAnnouncement & { body: string };

function NewsHero({ count }: { count: number }) {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-0">
      <div className="relative px-4 py-4">
        <div className="pointer-events-none absolute inset-x-4 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${C.gold} 50%, transparent 100%)` }} />
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xl font-semibold leading-none" style={{ color: C.text }}>{t('news.title')}</div>
            <div className="mt-2 text-xs leading-relaxed" style={{ color: C.muted }}>{t('news.subtitle')}</div>
          </div>
          <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl border" style={{ borderColor: `${C.gold}66`, background: C.chipBg, color: C.gold, boxShadow: `0 0 20px ${C.gold}18` }}>
            <svg viewBox="0 0 24 24" width="23" height="23" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h7M9 11h7M9 15h4" />
            </svg>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border px-3 py-2" style={{ borderColor: C.line, background: C.inputBg }}>
          <span className="text-xs" style={{ color: C.muted }}>{t('news.totalAnnouncements')}</span>
          <span className="font-mono text-sm font-semibold" style={{ color: C.gold }}>{count}</span>
        </div>
      </div>
    </ThemedPanel>
  );
}

function AnnouncementCard({ item, onOpen }: { item: Announcement; onOpen: () => void }) {
  const { t, i18n } = useTranslation();
  const date = formatAnnouncementDate(item.create_time, i18n.language);

  return (
    <ThemedPanel className="p-0 transition active:scale-[0.99]">
      <button
        type="button"
        onClick={onOpen}
        className="w-full cursor-pointer px-4 py-4 text-left"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <span className="flex size-6 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold" style={{ borderColor: `${C.gold}55`, background: C.chipBg, color: C.gold }}>
                {item.id}
              </span>
              <ThemedChip active>{t('news.announcement')}</ThemedChip>
            </div>
            <h2 className="line-clamp-2 text-base font-semibold leading-snug" style={{ color: C.text }}>
              {item.title}
            </h2>
          </div>
          <div className="mt-1 shrink-0" style={{ color: C.gold }}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 6l6 6-6 6" />
            </svg>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 border-t pt-3" style={{ borderColor: C.line }}>
          <time className="truncate text-[10px]" style={{ color: C.muted }}>{date}</time>
          <span className="text-[10px] font-semibold" style={{ color: C.gold }}>{t('news.viewDetail')}</span>
        </div>
      </button>
    </ThemedPanel>
  );
}

function NewsEmptyState() {
  const { t } = useTranslation();

  return (
    <ThemedPanel className="p-4">
      <EmptyState
        icon={
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 19.5A2.5 2.5 0 0 0 6.5 22H20" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 8h6M9 12h5" />
          </svg>
        }
        title={t('news.emptyTitle')}
        description={t('news.emptyDescription')}
        className="py-8"
      />
    </ThemedPanel>
  );
}

export function News() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Announcement | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn: () => configAPI.getAnnouncements(),
  });

  const rows = normalizeAnnouncements(data).map((item) => ({
    ...item,
    body: item.body ?? '',
  }));

  return (
    <div className="relative min-h-full" style={{ background: C.pageBg }}>
      <div className="relative mx-auto min-h-full w-full max-w-[430px]">
        <ThemedPageHeader title={t('news.title')} onBack={() => navigate(-1)} theme={C} />
        <div className="flex flex-col gap-4 overflow-y-auto px-5 pt-[70px]" style={{ height: 'calc(100% - 70px)', paddingBottom: '28px' }}>
          <NewsHero count={rows.length} />

          {isLoading ? (
            <LoadingSkeleton variant="list" lines={6} />
          ) : rows.length === 0 ? (
            <NewsEmptyState />
          ) : (
            <div className="flex flex-col gap-3">
              {rows.map((item) => (
                <AnnouncementCard
                  key={`${item.create_time}-${item.id}-${item.title}`}
                  item={item}
                  onOpen={() => setSelected(item)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <AnnouncementDetailModal item={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
